import React, { useEffect, useMemo, useState } from "react"
import { Button, Card, Col, Container, Nav, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { toast } from "react-toastify"
import { HiOutlineRefresh } from "react-icons/hi"
import { DragDropProvider } from "@dnd-kit/react"
import { move } from "@dnd-kit/helpers"
import { RestrictToVerticalAxis, disableDropAnimation } from "../../../helpers/dndkit"
import { useDestination, useUpdateDestination } from "../../../hooks/useDestinations"
import { useSelectedProject } from "../../../contexts/SelectedProjectContext"
import ConfirmationModal from "../../../components/confirmation"
import VideoModal from "../../../components/video-modal"
import { getLocaleValue } from "../../../helpers/getLocaleValue"
import { mapOrder } from "../../../helpers/mapOrder"
import { GuideModal } from "./components"
import { SortableGuideItem, isLocked } from "./components/SortableGuideItem"
import { GuidePreview } from "./components/GuidePreview"
import { useTeams } from "../../../hooks/useTeams"
import { useGuides, useCreateGuide, useDeleteGuide } from "../../../hooks/useGuides"
import { useAuth } from "../../../contexts/AuthContext"
import type { Guide } from "../../../constants/types"

type VideoModalState = {
  display: boolean
  video: Record<string, unknown> | null
}

const GuidesContainer: React.FC = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()

  const { data: teams = [] } = useTeams()
  const { selectedDestinationId } = useSelectedProject()
  const { data: destination } = useDestination(selectedDestinationId)
  const { data: allGuides, isLoading } = useGuides(selectedDestinationId)
  const { user } = useAuth()
  const createGuideMutation = useCreateGuide()
  const deleteGuideMutation = useDeleteGuide()
  const updateDestinationMutation = useUpdateDestination()

  const [language, setLanguage] = useState(i18n.resolvedLanguage ?? "en")
  const [selectedGuideId, setSelectedGuideId] = useState<string | null>(null)
  const [showGuideModal, setShowGuideModal] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [videoModal, setVideoModal] = useState<VideoModalState>({ display: false, video: null })

  const sortedGuides = useMemo(
    () => mapOrder(allGuides ?? [], destination?.order ?? undefined, "id"),
    [allGuides, destination?.order]
  )
  const [guides, setGuides] = useState(sortedGuides)

  useEffect(() => {
    setGuides(sortedGuides)
  }, [sortedGuides])

  const guide = (allGuides ?? []).find(x => x.id === selectedGuideId)
  const blocks = guide?.content?.blocks ?? []
  const role = teams.find(team => team.id === destination?.ownerTeamId)?.userRole

  const showToast = (type: "success" | "error", message: string, values?: Record<string, unknown>) => {
    const translatedMessage = t("guides.notifications." + message, values)
    if (type === "success") toast.success(translatedMessage)
    else if (type === "error") toast.error(translatedMessage)
  }

  const persistOrder = async (reordered: Guide[]) => {
    setGuides(reordered)
    if (!destination?.id) return
    const updatedOrder = reordered.map(x => x.id)
    try {
      await updateDestinationMutation.mutateAsync({ id: destination.id, data: { order: updatedOrder } })
    } catch {
      setGuides(sortedGuides)
      showToast("error", "sort.failed")
    }
  }

  const handleSelectGuide = (data: Guide) => {
    setSelectedGuideId(data.id)
  }

  const handleEdit = () => {
    if (selectedGuideId !== null) {
      history.push("/handbook/guides/" + selectedGuideId)
    }
  }

  const handleDelete = async () => {
    if (selectedGuideId && guide) {
      try {
        await deleteGuideMutation.mutateAsync(selectedGuideId)
        showToast("success", "deleteGuide.success", { topic: guide.title })
      } catch {
        showToast("error", "deleteGuide.failed")
      }
    }
    setShowConfirmationModal(false)
  }

  const handleCreate = async (values: Record<string, string>) => {
    setShowGuideModal(false)

    if (destination) {
      const data = {
        title: values.titleEn || values.titleFi,
        titleEn: values.titleEn,
        titleFi: values.titleFi,
        icon: values.icon,
        isDraft: values.isDraft,
        content: { time: Date.now(), blocks: [] },
        destination: destination.id,
        ownerTeamId: destination.ownerTeamId
      }

      try {
        const response = await createGuideMutation.mutateAsync(data)
        history.push("/handbook/guides/" + response.id)
      } catch {
        showToast("error", "createGuide.failed")
      }
    } else {
      showToast("error", "createGuide.failed")
    }
  }

  if (!destination || !user) {
    return null
  }

  return (
    <Container fluid>
      <GuideModal show={showGuideModal} onSubmit={handleCreate} onCancel={() => setShowGuideModal(false)} />
      <ConfirmationModal
        show={showConfirmationModal}
        title="delete"
        content="delete.guide"
        onOK={handleDelete}
        onCancel={() => setShowConfirmationModal(false)}
        danger
      />
      <Row>
        <Col>
          <h3 className="mb-4 font-weight-bold">{t("guides.title")}</h3>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={8} className="guides-container">
          <Card className="mb-4">
            <Card.Header>
              <Row>
                <Col xs={8}>
                  <h5 className="mb-0 mt-1">{getLocaleValue(destination, "name", language)}</h5>
                </Col>
                <Col xs={4}>
                  <Nav
                    justify
                    defaultActiveKey={language}
                    onSelect={lng => setLanguage(lng ?? "en")}
                    className="language-navigation">
                    <Nav.Item>
                      <Nav.Link eventKey="fi">{t("common.languages.fi")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="en">{t("common.languages.en")}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
              </Row>
            </Card.Header>
            <Card.Header className="button-group">
              <Button
                disabled={isLoading}
                onClick={() => setShowGuideModal(true)}
                className="btn-default btn-block-xs-only mb-1 mb-sm-0"
                variant="primary">
                {t("guides.button.toolbar.new")}
              </Button>
              <Button
                disabled={guide?.isLocked ?? true}
                onClick={() => setShowConfirmationModal(true)}
                className="float-sm-right btn-default btn-block-xs-only mb-1 mb-sm-0"
                variant="danger">
                {t("guides.button.toolbar.delete")}
              </Button>
              <Button
                disabled={isLocked(guide, role)}
                onClick={handleEdit}
                className="float-sm-right btn-default btn-block-xs-only mr-sm-1 mb-1 mb-sm-0"
                variant="primary">
                {t("guides.button.toolbar.edit")}
              </Button>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col>
                  {isLoading && (
                    <div className="loading-spinner">
                      <HiOutlineRefresh className="slow-spin icon loading-spinner-icon" />
                    </div>
                  )}
                  <DragDropProvider
                    modifiers={[RestrictToVerticalAxis]}
                    plugins={disableDropAnimation}
                    onDragEnd={event => {
                      if (event.canceled) return
                      persistOrder(move(guides, event))
                    }}>
                    {guides.map((g, index) => (
                      <SortableGuideItem
                        key={g.id}
                        guide={g}
                        index={index}
                        language={language}
                        role={role}
                        selected={g.id === selectedGuideId}
                        onClick={handleSelectGuide}
                      />
                    ))}
                  </DragDropProvider>
                  {!guides.length && <p className="text-center mt-3">{t("guides.noContent")}</p>}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={4} className="preview-container">
          <GuidePreview
            guide={guide}
            blocks={blocks}
            language={language}
            onVideoClick={(d) => setVideoModal({ display: true, video: d })}
          />
        </Col>
      </Row>
      <VideoModal
        video={videoModal.video}
        display={videoModal.display}
        close={() => setVideoModal({ display: false, video: null })}
      />
    </Container>
  )
}

export default GuidesContainer
